import {
  Button,
  Card,
  CardLevel,
  Drawer,
  DrawerBody,
  DrawerHeader,
  FormGroup,
  Input,
  ISelectOption,
  PaddingLevel,
  Select,
  Subheading2,
} from '@pinpointhq/thumbtack';
import * as React from 'react';
import { Address, Candidate } from '../../../../javascript/models';
import { createToast } from '../../../FlashToaster';
import { getCsrfToken } from '../../../shared/csrfToken';

const ELIGIBILITY_OPTIONS: ISelectOption[] = [
  { value: 'true', label: 'Eligible' },
  { value: 'false', label: 'Ineligible' },
];

// Matches JobSeeker eligibility_overwrite_reasons
const ELIGIBILITY_REASON_OPTIONS: ISelectOption[] = [
  { value: 'manual_eligibility_scrub', label: 'Manual eligibility check' },
  { value: 'university', label: 'University' },
  { value: 'graduation_year', label: 'Graduation year' },
  { value: 'ethnicity', label: 'Ethnicity' },
  { value: 'other', label: 'Other' },
];

interface IEditDrawer {
  candidate: Candidate;
  fetchData: () => void;
  handleClose: () => void;
}

export function EditDrawer({ candidate, fetchData, handleClose }: IEditDrawer) {
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const jobSeeker = (candidate.jobSeekers || [])[(candidate.jobSeekers || []).length - 1];

  const jsAddress1 = jobSeeker.addresses[0];
  const jsAddress2 = jobSeeker.addresses[1];
  const jsAddress3 = jobSeeker.addresses[2];

  // Address 1
  const [address1, setAddress1] = React.useState(jsAddress1 ? jsAddress1.address : '');
  const [postcode1, setPostcode1] = React.useState(jsAddress1 ? jsAddress1.postcode : '');
  const [longitude1, setLongitude1] = React.useState(jsAddress1 ? jsAddress1.longitude : '');
  const [latitude1, setLatitude1] = React.useState(jsAddress1 ? jsAddress1.latitude : '');

  // Address 2
  const [address2, setAddress2] = React.useState(jsAddress2 ? jsAddress2.address : '');
  const [postcode2, setPostcode2] = React.useState(jsAddress2 ? jsAddress2.postcode : '');
  const [longitude2, setLongitude2] = React.useState(jsAddress2 ? jsAddress2.longitude : '');
  const [latitude2, setLatitude2] = React.useState(jsAddress2 ? jsAddress2.latitude : '');

  // Address 3
  const [address3, setAddress3] = React.useState(jsAddress3 ? jsAddress3.address : '');
  const [postcode3, setPostcode3] = React.useState(jsAddress3 ? jsAddress3.postcode : '');
  const [longitude3, setLongitude3] = React.useState(jsAddress3 ? jsAddress3.longitude : '');
  const [latitude3, setLatitude3] = React.useState(jsAddress3 ? jsAddress3.latitude : '');

  const [firstName, setFirstName] = React.useState(candidate.firstName);
  const [lastName, setLastName] = React.useState(candidate.lastName);
  const [email, setEmail] = React.useState(candidate.email);
  const [phoneNumber, setPhoneNumber] = React.useState(candidate.phoneNumber);
  const [cvUploadKey, setCvUploadKey] = React.useState(null);
  const [eligible, setEligible] = React.useState<boolean>(candidate.eligible);
  const [eligibilityReason, setEligibilityReason] = React.useState<string>('manual_eligibility_scrub');

  const handleFirstNameChange = (event) => setFirstName(event.currentTarget.value);
  const handleLastNameChange = (event) => setLastName(event.currentTarget.value);
  const handleEmailChange = (event) => setEmail(event.currentTarget.value);
  const handlePhoneNumberChange = (event) => setPhoneNumber(event.currentTarget.value);

  const handleSave = () => {
    setIsSubmitting(true);
    candidate.firstName = firstName;
    candidate.lastName = lastName;
    candidate.email = email;
    candidate.phoneNumber = phoneNumber;
    candidate.cvUploadKey = cvUploadKey;
    if (eligible !== candidate.eligible) {
      candidate.eligible = eligible;
      candidate.eligibilityReason = eligibilityReason;
    }

    if (address1) {
      if (jobSeeker.addresses[0]) {
        jobSeeker.addresses[0].address = address1;
        jobSeeker.addresses[0].postcode = postcode1;
        jobSeeker.addresses[0].longitude = longitude1;
        jobSeeker.addresses[0].latitude = latitude1;
      } else {
        jobSeeker.addresses[0] = new Address({
          address: address1,
          postcode: postcode1,
          longitude: longitude1,
          latitude: latitude1,
        });
      }
    }

    if (address2) {
      if (jobSeeker.addresses[1]) {
        jobSeeker.addresses[1].address = address2;
        jobSeeker.addresses[1].postcode = postcode2;
        jobSeeker.addresses[1].longitude = longitude2;
        jobSeeker.addresses[1].latitude = latitude2;
      } else {
        jobSeeker.addresses[1] = new Address({
          address: address2,
          postcode: postcode2,
          longitude: longitude2,
          latitude: latitude2,
        });
      }
    }

    if (address3) {
      if (jobSeeker.addresses[2]) {
        jobSeeker.addresses[2].address = address3;
        jobSeeker.addresses[2].postcode = postcode3;
        jobSeeker.addresses[2].longitude = longitude3;
        jobSeeker.addresses[2].latitude = latitude3;
      } else {
        jobSeeker.addresses[2] = new Address({
          address: address3,
          postcode: postcode3,
          longitude: longitude3,
          latitude: latitude3,
        });
      }
    }

    candidate.save({ with: { jobSeekers: ['addresses'] } }).then((success) => {
      if (success) {
        createToast({ type: 'success', text: 'Successfully updated candidate' });
        fetchData();
        handleClose();
      } else {
        createToast({ type: 'error', text: 'Failed to save candidate' });
        setIsSubmitting(false);
      }
    });
  };

  async function handleUpload(event) {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('/uploads', {
        method: 'POST',
        headers: { 'X-CSRF-Token': getCsrfToken() || '' },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ Uploaded:', data);
      setCvUploadKey(data.key);
    } catch (err) {
      console.error(err);
      alert('Upload failed.');
    }
  }

  return (
    <Drawer maxWidth={600} isOpen={true}>
      <DrawerHeader text="Edit candidate" handleClose={handleClose} />
      <DrawerBody>
        <Card paddingLevel={PaddingLevel.NONE} level={CardLevel.NONE}>
          <FormGroup label="First name">
            <Input type="text" value={firstName} onChange={handleFirstNameChange} />
          </FormGroup>
          <FormGroup label="Last name">
            <Input type="text" value={lastName} onChange={handleLastNameChange} />
          </FormGroup>
          <FormGroup label="Email">
            <Input type="text" value={email} onChange={handleEmailChange} />
          </FormGroup>
          <FormGroup label="Phone number">
            <Input type="text" value={phoneNumber} onChange={handlePhoneNumberChange} />
          </FormGroup>
          <FormGroup
            label="Eligibility"
            helperText="Applies to their most recent application and stays even if the eligibility rules change"
          >
            <Select
              items={ELIGIBILITY_OPTIONS}
              activeItem={ELIGIBILITY_OPTIONS.find((option) => option.value === String(eligible))}
              onItemSelect={(item: ISelectOption) => setEligible(item.value === 'true')}
              buttonProps={{ large: true }}
            />
          </FormGroup>
          {eligible !== candidate.eligible && (
            <FormGroup label="Reason">
              <Select
                items={ELIGIBILITY_REASON_OPTIONS}
                activeItem={ELIGIBILITY_REASON_OPTIONS.find((option) => option.value === eligibilityReason)}
                onItemSelect={(item: ISelectOption) => setEligibilityReason(item.value)}
                buttonProps={{ large: true }}
              />
            </FormGroup>
          )}
          <FormGroup label="CV Upload">
            <input type="file" name="file" onChange={handleUpload} />
          </FormGroup>
          <Subheading2 className="mar-b-2">Addresses</Subheading2>
          <Card level={CardLevel.FILLED} paddingLevel={PaddingLevel.REGULAR} className="mar-b-2">
            <FormGroup label="Address">
              <Input type="text" value={address1} onChange={(e) => setAddress1(e.currentTarget.value)} />
            </FormGroup>
            <FormGroup label="Postcode">
              <Input type="text" value={postcode1} onChange={(e) => setPostcode1(e.currentTarget.value)} />
            </FormGroup>
            <FormGroup label="Latitude">
              <Input type="text" value={latitude1} onChange={(e) => setLatitude1(e.currentTarget.value)} />
            </FormGroup>
            <FormGroup label="Longitude">
              <Input type="text" value={longitude1} onChange={(e) => setLongitude1(e.currentTarget.value)} />
            </FormGroup>
          </Card>
          <Card level={CardLevel.FILLED} paddingLevel={PaddingLevel.REGULAR} className="mar-b-2">
            <FormGroup label="Address">
              <Input type="text" value={address2} onChange={(e) => setAddress2(e.currentTarget.value)} />
            </FormGroup>
            <FormGroup label="Postcode">
              <Input type="text" value={postcode2} onChange={(e) => setPostcode2(e.currentTarget.value)} />
            </FormGroup>
            <FormGroup label="Latitude">
              <Input type="text" value={latitude2} onChange={(e) => setLatitude2(e.currentTarget.value)} />
            </FormGroup>
            <FormGroup label="Longitude">
              <Input type="text" value={longitude2} onChange={(e) => setLongitude2(e.currentTarget.value)} />
            </FormGroup>
          </Card>
          <Card level={CardLevel.FILLED} paddingLevel={PaddingLevel.REGULAR} className="mar-b-2">
            <FormGroup label="Address">
              <Input type="text" value={address3} onChange={(e) => setAddress3(e.currentTarget.value)} />
            </FormGroup>
            <FormGroup label="Postcode">
              <Input type="text" value={postcode3} onChange={(e) => setPostcode3(e.currentTarget.value)} />
            </FormGroup>
            <FormGroup label="Latitude">
              <Input type="text" value={latitude3} onChange={(e) => setLatitude3(e.currentTarget.value)} />
            </FormGroup>
            <FormGroup label="Longitude">
              <Input type="text" value={longitude3} onChange={(e) => setLongitude3(e.currentTarget.value)} />
            </FormGroup>
          </Card>
          <Button text="Save" loading={isSubmitting} onClick={handleSave} large={true} fill={true} />
        </Card>
      </DrawerBody>
    </Drawer>
  );
}
